import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { consumeRedeemedFavorite } from '../services/pendingFavorite';
import radsportMarkWhite from '../assets/brand/radsport-mark-white.svg';

const FONT_HEAD = "'Archivo',sans-serif";
const FONT_MONO = "'Space Mono',monospace";
const FONT_BODY = "'Hanken Grotesk',sans-serif";

type State = 'confirming' | 'done' | 'failed';

/**
 * Zielseite des Bestaetigungslinks aus der Registrierungs-Mail (Double-Opt-in).
 * Loest den Token ein und meldet direkt an; danach geht es weiter zu den Events.
 */
const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { confirmEmail, resendConfirmation } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState<State>(token ? 'confirming' : 'failed');
  const [message, setMessage] = useState<string | null>(
    token ? null : 'Dieser Link ist unvollständig. Bitte öffne ihn direkt aus der E-Mail.'
  );
  const [email, setEmail] = useState('');
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  // Vor dem Login gemerktes Event, das beim Bestätigen automatisch favorisiert wurde.
  const [favoritedEventId, setFavoritedEventId] = useState<number | null>(null);
  // React 18 ruft Effekte im StrictMode doppelt auf — der Token ist aber einmalig,
  // der zweite Aufruf wuerde als "ungueltig" scheitern.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) return;
    startedRef.current = true;
    confirmEmail(token)
      .then(() => {
        // confirmEmail hat einen evtl. geparkten Merken-Klick schon eingelöst — ID fürs Redirect-Ziel abholen.
        setFavoritedEventId(consumeRedeemedFavorite());
        setState('done');
      })
      .catch((err: Error) => {
        setState('failed');
        setMessage(err.message);
      });
  }, [token, confirmEmail]);

  const handleResend = async () => {
    if (!email.trim()) return;
    setResendState('sending');
    await resendConfirmation(email.trim());
    setResendState('sent');
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#F5F3F3', fontFamily: FONT_BODY,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div className="animate-fade-up" style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
        <div style={{
          width: 64, height: 64, borderRadius: 18, background: '#922C57', margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 14px 30px rgba(146,44,87,0.35)',
        }}>
          <img src={radsportMarkWhite} alt="" width={38} height={38} style={{ display: 'block', objectFit: 'contain' }} />
        </div>

        {state === 'confirming' && (
          <>
            <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.2em', color: '#9A9494', marginTop: 18 }}>
              {'// '}KONTO AKTIVIEREN
            </div>
            <h1 style={{ fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 28, color: '#2C2C2C', margin: '10px 0 0' }}>
              Einen Moment…
            </h1>
            <p style={{ fontSize: 15, color: '#6A6A6A', marginTop: 10 }}>Wir bestätigen deine E-Mail-Adresse.</p>
          </>
        )}

        {state === 'done' && (
          <>
            <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.2em', color: '#9A9494', marginTop: 18 }}>
              {'// '}LOS GEHT'S
            </div>
            <h1 style={{
              fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 30, lineHeight: 1.05,
              color: '#2C2C2C', margin: '10px 0 0',
            }}>Willkommen an Bord!</h1>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#4A4A4A', marginTop: 12 }}>
              {favoritedEventId != null
                ? 'Deine E-Mail ist bestätigt und du bist angemeldet. Dein Event haben wir dir direkt auf die Merkliste gelegt.'
                : 'Deine E-Mail ist bestätigt und du bist angemeldet. Finde Events in deiner Nähe und speichere deine Favoriten.'}
            </p>
            <button
              type="button"
              onClick={() => navigate(favoritedEventId != null ? `/events/${favoritedEventId}` : '/')}
              style={{
                width: '100%', marginTop: 22, background: '#922C57', color: '#fff', border: 'none',
                padding: 15, borderRadius: 12, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 16,
                cursor: 'pointer', boxShadow: '0 8px 20px rgba(146,44,87,0.30)',
              }}
            >
              {favoritedEventId != null ? 'Zum gemerkten Event →' : 'Events entdecken →'}
            </button>
          </>
        )}

        {state === 'failed' && (
          <>
            <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.2em', color: '#9A9494', marginTop: 18 }}>
              {'// '}LINK UNGÜLTIG
            </div>
            <h1 style={{ fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 28, color: '#2C2C2C', margin: '10px 0 0' }}>
              Das hat nicht geklappt
            </h1>
            <div style={{
              marginTop: 20, background: '#fff', border: '1px solid #E5E1E1', borderRadius: 16,
              padding: 24, boxShadow: '0 4px 24px -4px rgba(0,0,0,0.08)', textAlign: 'left',
            }}>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: '#4A4A4A', margin: 0 }}>{message}</p>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: '#9A9494', marginTop: 14 }}>
                Links gelten 24 Stunden und lassen sich nur einmal verwenden. Trag deine Adresse ein,
                dann schicken wir dir einen neuen.
              </p>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="max@example.com"
                style={{
                  width: '100%', boxSizing: 'border-box', marginTop: 12, background: '#F5F3F3',
                  border: '1px solid #E5E1E1', borderRadius: 11, padding: '13px 14px',
                  fontFamily: FONT_BODY, fontSize: 15, color: '#2C2C2C', outline: 'none',
                }}
              />
              <button
                type="button"
                onClick={handleResend}
                disabled={resendState !== 'idle' || !email.trim()}
                style={{
                  width: '100%', marginTop: 12, background: resendState === 'sent' ? '#F6ECF0' : '#922C57',
                  color: resendState === 'sent' ? '#922C57' : '#fff', border: 'none', padding: 14,
                  borderRadius: 12, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 15,
                  cursor: resendState === 'idle' && email.trim() ? 'pointer' : 'default',
                  opacity: !email.trim() && resendState === 'idle' ? 0.6 : 1,
                }}
              >
                {resendState === 'idle' && 'Neuen Link senden'}
                {resendState === 'sending' && 'Wird gesendet…'}
                {resendState === 'sent' && 'Gesendet ✓'}
              </button>
            </div>
          </>
        )}

        <p style={{ marginTop: 22, fontSize: 14, color: '#6A6A6A' }}>
          <Link to="/login" style={{ fontWeight: 700, color: '#922C57', textDecoration: 'none' }}>
            Zur Anmeldung
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ConfirmEmail;
